import {
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	makeStyles,
} from "@material-ui/core";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import SkipNextIcon from "@material-ui/icons/SkipNext";
import { CalendarIcon, DatePicker } from "@mui/x-date-pickers";
import { get } from "lodash-es";
import React, { useEffect, useState } from "react";
import { getMomentTz } from "../../../services";
import { SearchShiftsParameters } from "../models/SearchShiftsParameters";
import { ShiftData } from "../models/shift-data";
import { getShiftsRangeByParameters } from "../repositories/ShiftsRepository";

const useStyles = makeStyles((theme) => ({
	container: {
		display: "grid",
		gridTemplateColumns:
			"auto minmax(100px, 1fr) 80px minmax(100px, 1fr) auto auto",
		alignItems: "center",
		width: "100%",
		margin: 0,
		padding: "8px 0",
		gap: "8px",
	},
	gridItem: {
		display: "flex",
		alignItems: "center",
	},
	datePickerWrapper: {
		minWidth: "100px",
		fontSize: ".8rem!important",
		margin: "0!important",
		"& .MuiInputAdornment-root": {
			margin: "0!important",
		},
		"& .MuiInputBase-input": {
			fontSize: "1rem!important",
			padding: "4px 8px !important",
		},
	},
	selectWrapper: {
		width: "80px",
		"& .MuiInputBase-input": {
			fontSize: "0.8rem !important",
			padding: "4px 8px !important",
			display: "inline-flex",
			alignItems: "center",
		},
	},
}));

interface PeriodOption {
	value: string;
	label: string;
}

type Props = {
	value: SearchShiftsParameters | null;
	onChange: (value: any) => void;
	showShiftAndCrews?: boolean;
	dateFormat?: string;
	periodOptions?: PeriodOption[];
};

const DEFAULT_PERIOD_OPTIONS: PeriodOption[] = [
	{ value: "Shift", label: "Shift" },
	{ value: "Day", label: "Day" },
	{ value: "4Days", label: "4 Days" },
	{ value: "Week", label: "Week" },
	{ value: "Month", label: "Month" },
];

const moment = getMomentTz();

export const ShiftPeriodNavigatorControl = ({
	value,
	onChange,
	showShiftAndCrews = true,
	dateFormat = "MM/DD/YYYY HH:mm a",
	periodOptions = DEFAULT_PERIOD_OPTIONS,
}: Props) => {
	const classes = useStyles();
	const [period, setPeriod] = useState(periodOptions[0]?.value || "Day");
	const [loadingShiftPeriodList, setLoadingShiftPeriodList] = useState(false);
	const [error, setError] = useState("");
	const [shiftPeriodList, setShiftPeriodList] = useState<ShiftData[]>([]);
	const [isActionInProgress, setIsActionInProgress] = useState(false);

	const handleArrowNavigation = async (direction: "next" | "prev") => {
		if (!startShift?.Start && !endShift?.End) return;

		try {
			if (direction === "prev" && startShift?.Start) {
				await searchShiftsRangeByParameters(
					period,
					moment(startShift.Start).toDate(),
					null,
					false,
					"prev",
				);
			} else if (direction === "next" && endShift?.End) {
				await searchShiftsRangeByParameters(
					period,
					null,
					moment(endShift.End).toDate(),
					false,
					"next",
				);
			}
		} catch (error) {
			setError(error as string);
		}
	};

	const handleLastShift = async () => {
		try {
			await searchShiftsRangeByParameters(period, null, null, false, "last");
		} catch (error) {
			setError(error as string);
		}
	};

	const searchShiftsRangeByParameters = async (
		newPeriod: string | null,
		newStartDate: Date | null,
		newEndDate: Date | null,
		useShiftIds: boolean | null = true,
		action: "prev" | "next" | "last" | null = null,
	) => {
		if (isActionInProgress) return;
		setIsActionInProgress(true);
		setLoadingShiftPeriodList(true);
		try {
			const res = await getShiftsRangeByParameters(
				newPeriod,
				newStartDate,
				newEndDate,
				useShiftIds ? (value?.StartShiftId ?? null) : null,
				useShiftIds ? (value?.EndShiftId ?? null) : null,
				action,
			);

			if (res.ok) {
				const rowsSource = get(res, "data.tables[0].rows", []);
				setShiftPeriodList(rowsSource);

				if (rowsSource.length >= 2) {
					onChange({
						StartShiftId: rowsSource[0].ShiftId,
						EndShiftId: rowsSource[1].ShiftId,
					});
				}
			} else {
				setError(res.message as string);
			}
		} catch (error) {
			setError(error as string);
		} finally {
			setLoadingShiftPeriodList(false);
			setIsActionInProgress(false);
		}
	};

	const handleDateChange = async (
		value: moment.Moment | null,
		key: "start" | "end",
	) => {
		if (!value || !value.isValid()) return;

		try {
			const newDate = value.toDate();

			if (key === "start") {
				await searchShiftsRangeByParameters(period, newDate, null, false);
			} else {
				await searchShiftsRangeByParameters(period, null, newDate, false);
			}
		} catch (error) {
			setError(error as string);
		}
	};

	const handleChangeScope = async (
		e: React.ChangeEvent<{ value: unknown }>,
	) => {
		const newPeriod = e.target.value as string;
		setPeriod(newPeriod);

		await searchShiftsRangeByParameters(
			newPeriod,
			null,
			endShift?.Start ? moment(endShift.Start).toDate() : null,
			false,
		);
	};

	useEffect(() => {
		if (value?.StartShiftId || value?.EndShiftId) {
			searchShiftsRangeByParameters(period, null, null, true);
		}
	}, [value?.StartShiftId, value?.EndShiftId]);

	useEffect(() => {
		searchShiftsRangeByParameters(period, null, null, true);
	}, []);

	const startShift = shiftPeriodList[0];
	const endShift = shiftPeriodList[1];

	const renderShiftCrewInfo = (shift: ShiftData | undefined) => {
		if (!showShiftAndCrews || !shift) return null;

		return (
			<span
				style={{
					paddingRight: 5,
					whiteSpace: "nowrap",
					overflow: "hidden",
					textOverflow: "ellipsis",
					fontSize: 15,
					fontWeight: "bold",
				}}
			>
				{shift.Shift} - {shift.Crew}
			</span>
		);
	};

	return (
		<div className={classes.container}>
			<div className={classes.gridItem}>
				<IconButton
					size="small"
					color="primary"
					disabled={loadingShiftPeriodList}
					onClick={() => handleArrowNavigation("prev")}
				>
					<ChevronLeft />
				</IconButton>
			</div>

			<div className={`${classes.gridItem} ${classes.datePickerWrapper}`}>
				<DatePicker
					label="Start"
					format={dateFormat}
					value={moment(startShift?.Start || moment().subtract(1, "day"))}
					onChange={(value) =>
						handleDateChange(value ? moment(value) : null, "start")
					}
					disabled={loadingShiftPeriodList}
					slots={{
						textField: (props) => (
							<TextField
								{...props}
								size="small"
								variant="outlined"
								fullWidth
								style={{ width: "100%" }}
								InputProps={{
									...props.InputProps,
									style: { paddingTop: "2px" },
								}}
							/>
						),
						openPickerIcon: () => (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									width: "100%",
								}}
							>
								{renderShiftCrewInfo(startShift)}
								<IconButton style={{ padding: "0px", flexShrink: 0 }}>
									<CalendarIcon />
								</IconButton>
							</div>
						),
					}}
				/>
			</div>

			<div className={`${classes.gridItem} ${classes.selectWrapper}`}>
				<FormControl variant="outlined" size="small" fullWidth>
					<InputLabel id="period-select-label">Period</InputLabel>
					<Select
						value={period}
						onChange={handleChangeScope}
						disabled={loadingShiftPeriodList}
						label="Period"
						labelId="period-select-label"
						style={{
							width: "100%",
						}}
					>
						{periodOptions.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</div>

			<div className={`${classes.gridItem} ${classes.datePickerWrapper}`}>
				<DatePicker
					label="End"
					format={dateFormat}
					value={moment(endShift?.Start || moment().subtract(1, "day"))}
					onChange={(value) =>
						handleDateChange(value ? moment(value) : null, "end")
					}
					disabled={loadingShiftPeriodList}
					slots={{
						textField: (props) => (
							<TextField
								{...props}
								size="small"
								variant="outlined"
								fullWidth
								style={{ width: "100%" }}
								InputProps={{
									...props.InputProps,
									style: { paddingTop: "2px" },
								}}
							/>
						),
						openPickerIcon: () => (
							<div
								style={{
									display: "flex",
									alignItems: "center",
									margin: "0 !important",
									width: "100%",
								}}
							>
								{renderShiftCrewInfo(endShift)}
								<IconButton style={{ padding: "0px", flexShrink: 0 }}>
									<CalendarIcon />
								</IconButton>
							</div>
						),
					}}
				/>
			</div>

			<div className={classes.gridItem}>
				<IconButton
					size="small"
					disabled={loadingShiftPeriodList}
					onClick={() => handleArrowNavigation("next")}
				>
					<ChevronRight />
				</IconButton>
			</div>

			<div className={classes.gridItem}>
				<IconButton
					size="small"
					disabled={loadingShiftPeriodList}
					onClick={handleLastShift}
				>
					<SkipNextIcon />
				</IconButton>
			</div>
		</div>
	);
};
